import { useState, useEffect } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HandCoins, CheckCircle2, Printer, Loader2, History } from "lucide-react";
import { toast } from "sonner";

export default function CollectOldDuesDialog({ open, onOpenChange, studentId, onCollected }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [dues, setDues] = useState([]);
  const [carriedId, setCarriedId] = useState("");
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [mode, setMode] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [allowDiscount, setAllowDiscount] = useState(true);

  const canDiscount = hasPerm(user, "add_discount");
  const token = localStorage.getItem("token");
  const current = dues.find((d) => d.id === carriedId);

  const load = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const r = await api.get(`/fees/preview/${studentId}`);
      const od = (r.data.fees.old_dues || []).filter((o) => o.remaining > 0);
      setStudent(r.data.student);
      setDues(od);
      const first = od[0];
      setCarriedId(first?.id || "");
      setAmount(first ? String(first.remaining) : "");
      api.get("/settings").then((s) => setAllowDiscount(s.data?.allow_discount !== false)).catch(() => {});
      api.get("/fees/next-receipt").then((rr) => setReceiptNo(rr.data.receipt_number)).catch(() => setReceiptNo(""));
    } catch (e) { toast.error("Failed to load old dues"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setReceipt(null); setDiscount(""); setRemarks(""); setMode("Cash");
      load();
    }
    // eslint-disable-next-line
  }, [open, studentId]);

  const pickDue = (id) => {
    setCarriedId(id);
    const d = dues.find((x) => x.id === id);
    setAmount(d ? String(d.remaining) : "");
  };

  const save = async () => {
    if (!carriedId) { toast.error("No old dues to collect"); return; }
    const amt = Number(amount || 0);
    if (amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (current && amt + Number(discount || 0) > current.remaining + 0.01) { toast.error("Amount cannot exceed the remaining old dues"); return; }
    setSaving(true);
    try {
      const r = await api.post("/fees/collect-old-dues", {
        student_id: studentId, carried_id: carriedId, amount_paid: amt,
        discount: Number(discount || 0), payment_mode: mode, remarks, receipt_number: receiptNo,
      });
      setReceipt(r.data);
      toast.success(`Receipt ${r.data.receipt_number} generated`);
      onCollected?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remaining = current ? current.remaining - Number(amount || 0) - Number(discount || 0) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="collect-old-dues-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><History size={20} className="text-amber-600" />Collect Old Dues</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-indigo-500" /></div>
        ) : receipt ? (
          <div className="p-4 text-center" data-testid="old-dues-receipt-success">
            <CheckCircle2 size={44} className="mx-auto text-emerald-500 mb-2" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Old Dues Paid</h3>
            <p className="text-slate-500 mt-1">Receipt <span className="font-mono font-semibold text-slate-700">{receipt.receipt_number}</span></p>
            <div className="text-2xl font-heading font-extrabold text-slate-900 my-3 tabular">{fmt(receipt.amount_paid)}</div>
            <div className="flex gap-2 justify-center">
              <a href={`${API}/receipts/${receipt.id}/pdf?token=${token}`} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="old-dues-print-receipt"><Printer size={16} className="mr-2" />Print / Download</Button>
              </a>
              <Button onClick={() => onOpenChange(false)} data-testid="old-dues-done-btn">Done</Button>
            </div>
          </div>
        ) : dues.length === 0 ? (
          <div className="py-10 text-center text-slate-400" data-testid="old-dues-none">No old dues to collect for this student.</div>
        ) : (
          <div className="space-y-3 pt-1">
            {student && (
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                <div className="font-semibold text-slate-900">{student.name}</div>
                <div className="text-xs text-slate-500">{student.admission_number} · {student.class_name} {student.section}</div>
              </div>
            )}
            {dues.length > 1 && (
              <div>
                <Label>Old Dues Entry</Label>
                <Select value={carriedId} onValueChange={pickDue}>
                  <SelectTrigger data-testid="old-dues-entry-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{dues.map((d) => <SelectItem key={d.id} value={d.id}>{d.label} ({d.from_session}) — {fmt(d.remaining)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {current && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                <span className="text-amber-700">{current.label} <span className="text-xs text-slate-500">(from {current.from_session || "—"})</span></span>
                <span className="font-bold tabular text-amber-700" data-testid="old-dues-remaining">{fmt(current.remaining)}</span>
              </div>
            )}
            <div>
              <Label>Receipt No.</Label>
              <Input value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)} className="font-mono" data-testid="old-dues-receipt-no" />
            </div>
            <div>
              <Label>Amount Being Paid</Label>
              <Input type="number" className="h-11 text-lg font-bold tabular" value={amount}
                onChange={(e) => { const raw = e.target.value; if (raw === "") return setAmount(""); setAmount(String(Math.min(Number(raw), current ? current.remaining : Number(raw)))); }}
                data-testid="old-dues-amount" />
              <button type="button" onClick={() => setAmount(String(current?.remaining || 0))} className="text-xs text-indigo-600 hover:underline mt-1" data-testid="old-dues-full">Full Remaining</button>
            </div>
            {canDiscount && allowDiscount && (
              <div><Label>Discount</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="old-dues-discount" /></div>
            )}
            <div><Label>Payment Mode</Label>
              <Select value={mode} onValueChange={setMode}><SelectTrigger data-testid="old-dues-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "UPI", "Bank Transfer", "Cheque", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="old-dues-remarks" /></div>
            <div className="flex justify-between text-sm bg-slate-50 rounded-lg p-3">
              <span className="text-slate-500">Remaining after payment</span>
              <span className={`font-bold tabular ${remaining > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(remaining)}</span>
            </div>
            <Button className="w-full h-11" onClick={save} disabled={saving} data-testid="save-old-dues-btn">
              {saving ? <Loader2 className="animate-spin" /> : <><HandCoins size={18} className="mr-2" />Collect Old Dues</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
